import React, { useMemo, useState } from 'react';
import { BuroPortal, AVISO_SIN_CLIENTE } from './helpers';
import { AvisoCodigoPostal, BuroCampo } from './BuroCampo';
import { useAutollenadoDomicilio } from './useCodigoPostal';
import { clavesDomicilio } from './columnasPm';
import { erroresDe } from './columnas';

const NOMBRES = ['1004', '1005', '1006', '1007'];
const TIPOS = ['PF', 'PFAE', 'PM'];

/*
 * Ficha de la empresa (PM) o de la persona física con actividad empresarial (PFAE):
 * datos del segmento EM y tipo de persona. Se edita sobre la primera fila y el
 * backend propaga los datos a todos los contratos de la empresa.
 *
 * props:
 *   filas        contratos de la empresa en el reporte (la primera da los valores)
 *   columnas     columnas EM (columnasPm().tabs.empresa)
 *   cola         { pos, total, siguiente } cuando se abrió desde «Empresas con datos faltantes»
 *   onGuardar    recibe [{columna, valor}] solo con lo que cambió (columna 0 = tipo de persona)
 *                y si va solo al reporte
 */
export const BuroFichaEmpresa = ({ filas, columnas, cola, guardando, error, onClose, onGuardar }) => {
  const fila = filas[0];
  // Nombre legible para identificar la empresa en el encabezado y en cada mensaje
  // de error. `fila.cliente` es el nombre armado por el backend (siempre viene);
  // si faltara, se cae a la razón social (EM.03 = 1003) o al RFC (EM.00 = 1000).
  const nombreEmpresa = String(
    fila.cliente || (fila.valores || {})['1003'] || (fila.valores || {})['1000'] || '',
  ).trim();
  const inicial = fila.tipo_persona || (String((fila.valores || {})['1023']) === '2' ? 'PFAE' : 'PM');
  const [valores, setValores] = useState(() => ({ ...fila.valores }));
  const [tipo, setTipo] = useState(inicial);
  const [sistema, setSistema] = useState(!!fila.cliente_ligado);
  // Un tipo sin confirmar solo se confirma mandándolo, aunque no cambie.
  const sinConfirmar = fila.tipo_confirmado === false;
  // C.P. completo y encontrado → estado, municipio, ciudad y colonia se llenan solos
  // (siguen editables); la calle y el complemento se capturan a mano.
  const colCp = columnas.find((c) => c.dom === 'cp');
  const claves = useMemo(() => clavesDomicilio(colCp), [colCp]);
  const cp = useAutollenadoDomicilio(colCp ? valores[colCp.k] : '', claves, setValores);
  const fisica = tipo !== 'PM';
  const visibles = columnas.filter((c) => !c.fija && c.k !== 'tipo_persona' && (fisica ? c.k !== '1003' : !NOMBRES.includes(c.k)));
  // Obligatorios vacíos: para el resumen de arriba y para saber si la ficha está completa.
  const faltantes = visibles.filter((c) => c.obligatorio && !String(valores[c.k] ?? '').trim());
  const cambios = visibles
    .filter((c) => String(valores[c.k] ?? '') !== String(fila.valores[c.k] ?? ''))
    .map((c) => ({ columna: Number(c.k), valor: valores[c.k] ?? '' }));
  if (tipo && (tipo !== inicial || sinConfirmar)) cambios.push({ columna: 0, valor: tipo });

  return <BuroPortal><div className="bu-panel" style={{ width: 520 }}>
    <div className="bu-panel-head">
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 style={{ margin: 0 }}>{tipo === 'PM' ? 'Empresa' : tipo === 'PFAE' ? 'Persona física con actividad empresarial' : 'Persona física'}</h3>
        {nombreEmpresa && <div className="s" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nombreEmpresa}</div>}
      </div>
      {cola && <span className="bu-chip" style={{ alignSelf: 'center', background: '#FDECEC', color: '#B91C1C' }}>Empresa {cola.pos} de {cola.total}</span>}
      <button type="button" className="bu-btn" onClick={onClose}>Cerrar</button>
    </div>
    <div className="bu-panel-body">
      <div className="bu-sub">{filas.length} contrato(s). RFC de {fisica ? 13 : 12} caracteres.</div>
      {error && <div role="alert" className="bu-note bu-note-err" style={{ whiteSpace: 'pre-line' }}>{error}</div>}
      {faltantes.length > 0 && (
        <div className="bu-note bu-note-warn">
          <i className="pi pi-exclamation-triangle" />
          <span>
            {nombreEmpresa ? <><strong>{nombreEmpresa}</strong>: faltan </> : 'Faltan '}
            {faltantes.length === 1 ? 'un dato obligatorio' : `${faltantes.length} datos obligatorios`}
            {' '}({faltantes.map((c) => c.num).join(', ')}).
          </span>
        </div>
      )}
      <div className="bu-ficha-sec" style={{ gap: 10 }}>
        <h4>Tipo de persona</h4>
        <div style={{ display: 'flex', gap: 20 }}>
          {TIPOS.map((t) => (
            <label key={t} className="bu-radio" style={{ gap: 8 }}>
              <input type="radio" name="tipo-empresa" checked={tipo === t} disabled={guardando} onChange={() => setTipo(t)} />{t}
            </label>
          ))}
        </div>
        {sinConfirmar && <div className="bu-note bu-note-warn"><i className="pi pi-info-circle" /><span>El tipo está sin confirmar: al guardar se confirma el que quede elegido.</span></div>}
        {tipo === 'PF' && <div className="bu-sub" style={{ fontSize: 12 }}>Va en el reporte de Personas Físicas. Sus contratos saldrán de este reporte.</div>}
      </div>
      {visibles.map((c) => <label className="bu-field" key={c.k}><span className="bu-label">{c.label} · {c.num}{c.obligatorio ? ' *' : ''}</span><BuroCampo col={c} disabled={guardando} invalid={c.obligatorio && !String(valores[c.k] ?? '').trim()} className={c.obligatorio && !String(valores[c.k] ?? '').trim() ? 'bu-input bu-req-falta' : 'bu-input'} value={valores[c.k] ?? ''} onChange={(v) => setValores((prev) => ({ ...prev, [c.k]: v }))} colonias={c.dom === 'colonia' && cp.estado === 'ok' ? cp.datos.colonias : null} />{c.dom === 'cp' && <AvisoCodigoPostal cp={cp} />}{erroresDe(fila, c).map((e, i) => <span className="bu-msg err" key={i}>{nombreEmpresa ? `${nombreEmpresa} — ${e.mensaje}` : e.mensaje}</span>)}</label>)}
      {fila.cliente_ligado ? <label className="bu-check"><input type="checkbox" checked={sistema} onChange={(e) => setSistema(e.target.checked)} />Corregir también en el sistema</label> : <div className="bu-note bu-note-warn">{AVISO_SIN_CLIENTE}</div>}
      <div className="bu-sub">Los datos de empresa cambian en todas sus filas del reporte.</div>
    </div>
    <div className="bu-panel-foot">
      {cola?.siguiente && <span className="bu-sub" style={{ fontSize: 12, marginRight: 'auto' }}>Siguiente: {cola.siguiente}</span>}
      <button type="button" className="bu-btn" onClick={onClose}>Cancelar</button>
      <button type="button" className="bu-btn bu-btn-primary" disabled={guardando || !visibles.length} onClick={() => onGuardar(cambios, !sistema)}>{guardando ? 'Guardando…' : cola ? 'Guardar y siguiente' : 'Guardar empresa'}</button>
    </div>
  </div></BuroPortal>;
};
